import React, { useState, useEffect, useCallback, useMemo } from "react";
import mailIcon from "../../assets/icon/mail_icon.png";
import apiService from "../../actions/apiService";
import { formatStatusLabel } from "../../utils/statusUtils";

const Backdrop = ({ onClick }) => (
  <div
    className="fixed inset-0 bg-black/30 z-[110]"
    onClick={onClick}
    aria-hidden
  />
);

const ModalContainer = ({ children }) => (
  <div className="fixed mt-[10px] mb-[10px] inset-0 z-[110] flex items-center justify-center">
    <div className="w-full rounded-[12px] overflow-y-auto h-full max-w-[852px] bg-white overflow-hidden px-5 md:px-16 py-5 md:py-8">
      {children}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-rubik text-big-price md:text-big-price-d text-esdarkGray !font-[500]">
    {children}
  </h3>
);

const Field = ({ label, children }) => (
  <div className="w-full">
    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
      {label}
    </label>
    {children}
  </div>
);

const TransactionManagementModal = ({
  open,
  onClose,
  transactionData,
  itemId,
  onInviteSent,
}) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [itemDetails, setItemDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [error, setError] = useState(null);

  const itemRecord = useMemo(() => {
    if (itemDetails?.item) return itemDetails.item;
    if (itemDetails) return itemDetails;
    if (transactionData?.item) return transactionData.item;
    return transactionData || null;
  }, [itemDetails, transactionData]);

  const fetchItemDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/items/${itemId}`);
      if (response.success && response.data) {
        setItemDetails(response.data);
      } else {
        console.error("Error fetching item details:", response.error);
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  // Fetch item details when modal opens
  useEffect(() => {
    if (open && itemId) {
      fetchItemDetails();
    }
  }, [open, itemId, fetchItemDetails]);

  // Clear form fields and error when modal closes
  useEffect(() => {
    if (!open) {
      setInviteEmail("");
      setPersonalMessage("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !itemId) return;

    setSendingInvitation(true);
    setError(null);

    const payload = {
      item_id: itemId,
      buyer_email: inviteEmail.trim(),
      buyer_name: inviteEmail.split("@")[0],
      message: personalMessage || "",
    };

    try {
      const res = await apiService.post("/invites", payload);
      if (res.success) {
        // Do not fetch invites here; just reset form per request
        setInviteEmail("");
        setPersonalMessage("");

        // Refresh item details to show the new invite
        fetchItemDetails();

        // Notify parent component to refresh listings
        if (onInviteSent) {
          onInviteSent();
        }

        setError("Invitation sent successfully");
      } else {
        console.error("Failed to send invite:", res.error);
        setError(`${res.error || "Failed to send invite"}`);
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      setError("Failed to send invitation. Please try again.");
    } finally {
      setSendingInvitation(false);
    }
  };

  return (
    <>
      <Backdrop onClose={onClose} />
      <ModalContainer>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading item details...</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className="pb-8 flex items-center justify-between">
              <div>
                <h3 className="text-page-title font-rubik md:text-page-title-d font-semibold text-[#1E1E1E]">
                  Send invitation
                </h3>
                <p className="text-[18px] font-[400] opacity-50 mt-4">
                  Invite potential buyers to make an offer.
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="h-9 w-9 rounded-[8px] hover:bg-[#F4F6F8] grid place-items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 6L18 18M6 18L18 6"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1">
              {/* Invitations */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <SectionTitle>Invitations</SectionTitle>

                <form
                  onSubmit={handleSendInvitation}
                  className="space-y-4 mt-4"
                >
                  <Field label="Potential new buyer email">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="e.g.: buyer@email.com"
                      className="w-full py-3 text-[14px] font-[400] opacity-75 rounded-[8px] border border-[#00000026] px-3"
                    />
                  </Field>
                  <Field label="Personal message (optional)">
                    <textarea
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      placeholder="Add a personal message to your invitation..."
                      rows={3}
                      className="w-full py-3 text-[14px] font-[400] opacity-75 rounded-[8px] border border-[#00000026] px-3"
                    />
                  </Field>
                  {error && (
                    <p
                      className={`text-sm ${
                        error.includes("successfully")
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={sendingInvitation}
                    className={`w-full bg-blue-gradient text-white px-4 py-3 rounded-[8px] text-[14px] font-[600] flex items-center justify-center gap-2 ${
                      sendingInvitation ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {sendingInvitation && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    {sendingInvitation
                      ? "Sending invitation..."
                      : "Send invitation"}
                  </button>
                </form>

                {/* Invited Buyers List */}

                {(itemDetails?.item?.invites || itemRecord?.invites || []).map(
                  (buyer) => (
                    <div className="mt-8">
                      <div className="space-y-4">
                        <div
                          key={buyer.id}
                          className="border border-[#DCEDFF] rounded-[12px] p-5"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                              <div className="rounded-full">
                                <img alt="mail" src={mailIcon} />
                              </div>
                              <div className="flex-1">
                                <p className="font-[600] text-[14px] opacity-75 text-esdarkGray">
                                  Potential buyer invited
                                </p>
                                <p className="text-[16px] font-[400] opacity-50 text-[#000025]">
                                  {/* {buyer.message} */}
                                  {buyer.buyer_email}
                                </p>
                                <p className="text-[12px] mt-2 font-[400] opacity-50 text-[#000025]">
                                  {buyer.message || "No message provided"}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`px-[10px] py-1 rounded-[50px] text-[14px] font-[600] ${
                                buyer.status === "Listed"
                                  ? "bg-[#E0E0E0] text-[#575757]"
                                  : "bg-[#000025] text-[#ffffff]"
                              }`}
                            >
                              {formatStatusLabel(buyer.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </ModalContainer>
    </>
  );
};

export default TransactionManagementModal;
